import { useEffect, useState } from "react";
import { api, NodeTypeTemplate } from "./api";

export type NodeTypeConfig = {
  id: string;
  label: string;
  defaultColor: string;
  defaultTextColor?: string;
  shape?: string;
  icon?: string;
  category?: string;
};

// Fallback default types (used if API fails)
const FALLBACK_NODE_TYPES: NodeTypeConfig[] = [
  { id: "root", label: "Root", defaultColor: "#1e3a8a", defaultTextColor: "#ffffff", category: "core" },
  { id: "hauptgremium", label: "Hauptgremium", defaultColor: "#0f766e", defaultTextColor: "#ffffff", category: "core" },
  { id: "gruppe", label: "Gruppe", defaultColor: "#2563eb", defaultTextColor: "#ffffff", category: "group" },
  { id: "ortsgruppe", label: "Ortsgruppe", defaultColor: "#7c3aed", defaultTextColor: "#ffffff", category: "group" },
  { id: "arbeitskreis", label: "Arbeitskreis", defaultColor: "#ea580c", defaultTextColor: "#ffffff", category: "work" },
  { id: "projektgruppe", label: "Projektgruppe", defaultColor: "#16a34a", defaultTextColor: "#ffffff", category: "work" },
  { id: "fraktion", label: "Fraktion", defaultColor: "#be123c", defaultTextColor: "#ffffff", category: "politics" },
  { id: "linksjugend", label: "Linksjugend", defaultColor: "#be4bdb", defaultTextColor: "#ffffff", category: "politics" },
];

// Convert NodeTypeTemplate to NodeTypeConfig
function templateToConfig(template: NodeTypeTemplate): NodeTypeConfig {
  return {
    id: template.key,
    label: template.label,
    defaultColor: template.default_color,
    defaultTextColor: template.default_text_color,
    shape: template.shape,
    icon: undefined, // Can be extended later
    category: undefined, // Can be extended later
  };
}

/**
 * Hook to load node types from the API.
 * Falls back to hardcoded types if the API fails.
 *
 * @param structureId - Optional structure ID to filter templates by visibility (recommended)
 * @param structureTypeKey - Optional structure type key to filter templates (fallback)
 */
export function useNodeTypes(structureId?: string, structureTypeKey?: string) {
  const [nodeTypes, setNodeTypes] = useState<NodeTypeConfig[]>(FALLBACK_NODE_TYPES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTypes = async () => {
      try {
        const templates = await api.listTemplates(false, structureTypeKey, structureId);
        if (isMounted) {
          const configs = templates.map(templateToConfig);
          setNodeTypes(configs);
          setError(null);
        }
      } catch (err: any) {
        console.warn("Failed to load node types from API, using fallback:", err);
        if (isMounted) {
          setError(err.message || "Fehler beim Laden der Typen");
          // Keep fallback types
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTypes();

    return () => {
      isMounted = false;
    };
  }, [structureId, structureTypeKey]);

  const nodeTypeById = (id?: string) => nodeTypes.find((t) => t.id === id);

  const refetch = async () => {
    setLoading(true);
    try {
      const templates = await api.listTemplates(false, structureTypeKey, structureId);
      const configs = templates.map(templateToConfig);
      setNodeTypes(configs);
      setError(null);
    } catch (err: any) {
      console.warn("Failed to reload node types:", err);
      setError(err.message || "Fehler beim Laden der Typen");
    } finally {
      setLoading(false);
    }
  };

  return { nodeTypes, nodeTypeById, loading, error, refetch };
}
